/**
 * API client cho backend PHP self-host.
 *
 * Cấu hình:
 *   - Khi self-host: build với VITE_API_BASE="/api" (Apache cùng domain).
 *   - Khi dev local trỏ về server thật: VITE_API_BASE="http://192.168.x.x/api".
 *   - Mặc định "/api" → hợp với deploy Apache cùng vhost.
 *
 * Auth: dùng cookie session PHP (HttpOnly). credentials: "include" bắt buộc.
 * Timeout: mặc định 15s để tránh request treo vô hạn làm UI no-response.
 */

import { IS_DEMO, handleDemo } from "./demo-api";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") || "/api";
// Mặc định 20s cho GET/POST/PUT/PATCH/DELETE thường.
// Upload (api.upload) đặt timeoutMs=0 để KHÔNG tự ngắt — phụ thuộc kích thước file
// và tốc độ mạng người dùng; Apache/PHP vẫn có timeout riêng làm chốt chặn cuối.
// Mọi method đều cho phép override qua tham số { timeoutMs } để các call chậm
// (push, jobs Piwigo, backup) tự đặt thời gian phù hợp.
const DEFAULT_TIMEOUT_MS = 20_000;

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

function buildUrl(path: string, query?: Query): string {
  const url = `${BASE}${path.startsWith("/") ? path : `/${path}`}`;
  if (!query) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined) continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

async function request<T>(method: string, path: string, opts?: {
  query?: Query;
  json?: unknown;
  form?: FormData;
  signal?: AbortSignal;
  timeoutMs?: number;
}): Promise<T> {
  // Demo mode (Lovable preview): chặn fetch, dùng in-memory mock.
  if (IS_DEMO) {
    return handleDemo<T>(method, path, { query: opts?.query, json: opts?.json, form: opts?.form });
  }
  const headers: Record<string, string> = { Accept: "application/json" };
  let body: BodyInit | undefined;

  if (opts?.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.json);
  } else if (opts?.form) {
    body = opts.form;
  }

  // Hỗ trợ timeout + abort signal kết hợp.
  const ctl = new AbortController();
  const timeoutMs = opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timer = timeoutMs > 0 ? setTimeout(() => ctl.abort(), timeoutMs) : null;
  if (opts?.signal) {
    if (opts.signal.aborted) ctl.abort();
    else opts.signal.addEventListener("abort", () => ctl.abort(), { once: true });
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts?.query), {
      method,
      headers,
      body,
      credentials: "include",
      signal: ctl.signal,
    });
  } catch (e: any) {
    if (timer) clearTimeout(timer);
    if (e?.name === "AbortError") {
      throw new ApiError(0, `Yêu cầu API quá ${timeoutMs}ms — kiểm tra server PHP / mạng.`);
    }
    throw new ApiError(0, e?.message || "Network error");
  }
  if (timer) clearTimeout(timer);

  const ct = res.headers.get("content-type") || "";
  const isJson = ct.includes("application/json");
  const payload: any = isJson ? await res.json().catch(() => null) : await res.text().catch(() => "");

  if (!res.ok) {
    const msg = (isJson && payload && (payload.message || payload.error)) || res.statusText || `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, payload);
  }
  return payload as T;
}

type Extra = { timeoutMs?: number; signal?: AbortSignal };

export const api = {
  get:    <T>(path: string, query?: Query, extra?: Extra)   => request<T>("GET",    path, { query, ...extra }),
  post:   <T>(path: string, json?: unknown, extra?: Extra)  => request<T>("POST",   path, { json,  ...extra }),
  put:    <T>(path: string, json?: unknown, extra?: Extra)  => request<T>("PUT",    path, { json,  ...extra }),
  patch:  <T>(path: string, json?: unknown, extra?: Extra)  => request<T>("PATCH",  path, { json,  ...extra }),
  del:    <T>(path: string, query?: Query, extra?: Extra)   => request<T>("DELETE", path, { query, ...extra }),
  // Upload: mặc định không timeout (0). Caller có thể override nếu muốn giới hạn.
  upload: <T>(path: string, form: FormData, extra?: Extra)  => request<T>("POST",   path, { form, timeoutMs: 0, ...extra }),
};

export const API_BASE = BASE;
